import { SensorType, DeviceType, SENSOR_CONFIGS } from '@grosync/common';

/* ---- 센서 표시 메타 (실제 SensorType 키) ----------------------------- */
export interface SensorMetaEntry {
  label: string;
  icon: string;
  tone: string;
}

export const SENSOR_META: Record<string, SensorMetaEntry> = {
  [SensorType.TEMPERATURE]: { label: '온도', icon: 'thermostat', tone: '#E07B3E' },
  [SensorType.HUMIDITY]: { label: '습도', icon: 'humidity_percentage', tone: '#2069C7' },
  [SensorType.ILLUMINANCE]: { label: '조도', icon: 'light_mode', tone: '#C77B20' },
  [SensorType.CO2]: { label: 'CO₂', icon: 'co2', tone: '#178A50' },
  [SensorType.ATMOSPHERIC_PRESSURE]: { label: '대기압', icon: 'compress', tone: '#6A7A8A' },
  [SensorType.UV_INDEX]: { label: '자외선(UV)', icon: 'wb_sunny', tone: '#C79320' },
  [SensorType.WIND_SPEED]: { label: '풍속', icon: 'air', tone: '#4A85DD' },
  [SensorType.WIND_DIRECTION]: { label: '풍향', icon: 'explore', tone: '#4A85DD' },
  [SensorType.RAINFALL]: { label: '강우량', icon: 'rainy', tone: '#2E7D9A' },
  [SensorType.SOIL_MOISTURE]: { label: '토양수분', icon: 'water', tone: '#2E7D9A' },
  [SensorType.SOIL_TEMPERATURE]: { label: '지온', icon: 'device_thermostat', tone: '#B5562E' },
  [SensorType.EC]: { label: 'EC', icon: 'bolt', tone: '#7C6428' },
  [SensorType.PH]: { label: 'pH', icon: 'science', tone: '#6A4FB0' },
  [SensorType.LEAF_WETNESS]: { label: '엽면수분', icon: 'dew_point', tone: '#2069C7' },
};

export function sensorMeta(type: string): SensorMetaEntry {
  return SENSOR_META[type] ?? { label: type, icon: 'sensors', tone: 'var(--md-sys-color-primary)' };
}

/** SENSOR_CONFIGS 기반 기본 단위/범위 (모달 자동완성용) */
export function sensorDefaults(type: string): { unit: string; min?: number; max?: number } {
  const cfg = SENSOR_CONFIGS[type as keyof typeof SENSOR_CONFIGS];
  return cfg
    ? { unit: cfg.unit, min: cfg.validMin, max: cfg.validMax }
    : { unit: '' };
}

export const SENSOR_TYPE_OPTIONS: string[] = Object.values(SensorType);

/* ---- 장비 유형 ------------------------------------------------------- */
export const DEVICE_TYPE_LABELS: Record<string, string> = {
  [DeviceType.SENSOR_NODE]: '센서 노드',
  [DeviceType.GATEWAY]: '게이트웨이',
  [DeviceType.ACTUATOR]: '액추에이터',
};
export const DEVICE_TYPE_OPTIONS: string[] = Object.values(DeviceType);
export function deviceTypeLabel(t: string): string {
  return DEVICE_TYPE_LABELS[t] ?? t;
}

/* ---- 생육 수집 항목 9종 ---------------------------------------------- */
export interface GrowthFieldEntry {
  value: string;
  label: string;
  icon: string;
  desc: string;
}

export const GROWTH_FIELD_CATALOG: GrowthFieldEntry[] = [
  { value: 'plantHeight', label: '초장', icon: 'height', desc: 'cm 단위 식물 높이' },
  { value: 'leafCount', label: '엽수', icon: 'eco', desc: '전개된 잎 개수' },
  { value: 'stemThickness', label: '줄기굵기', icon: 'line_weight', desc: '줄기 직경(mm)' },
  { value: 'leafArea', label: '엽면적', icon: 'spa', desc: '잎 면적(cm²)' },
  { value: 'fruitCount', label: '과실수', icon: 'nutrition', desc: '착과·수확 과실 수' },
  { value: 'flowerCluster', label: '화방수', icon: 'local_florist', desc: '화방(꽃송이) 수' },
  { value: 'healthScore', label: '건강점수', icon: 'favorite', desc: '1~5점 주관 평가' },
  { value: 'healthNotes', label: '건강노트', icon: 'edit_note', desc: '이상 소견 텍스트' },
  { value: 'photo', label: '생육사진', icon: 'photo_camera', desc: '사진 첨부 여부' },
];

export function growthFieldMeta(value: string): GrowthFieldEntry {
  return (
    GROWTH_FIELD_CATALOG.find((g) => g.value === value) ?? {
      value,
      label: value,
      icon: 'analytics',
      desc: '',
    }
  );
}

/** 구버전(한글 라벨로 저장된) 생육 항목 → 현재 카탈로그 키 매핑 */
const LEGACY_GROWTH_LABELS: Record<string, string> = {
  '초장': 'plantHeight',
  '엽면적': 'leafArea',
  '줄기굵기': 'stemThickness',
  '엽수': 'leafCount',
  '화방수': 'flowerCluster',
};

/**
 * growthFields 항목 1개를 정규화한다. 현재 카탈로그 키면 그대로,
 * 구버전 한글 라벨이면 대응 키로, 그 외(예: __meta__ 문자열)는 null.
 */
export function normalizeGrowthField(value: string): string | null {
  if (GROWTH_FIELD_CATALOG.some((g) => g.value === value)) return value;
  return LEGACY_GROWTH_LABELS[value] ?? null;
}

/** growthFields 배열 정규화 + 중복 제거 (정의 순서 유지) */
export function normalizeGrowthFields(values: string[] | undefined): string[] {
  const out: string[] = [];
  for (const v of values ?? []) {
    const key = normalizeGrowthField(v);
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}
